import { assert, lang } from 'zf-infra';
import axios from 'axios';
import { CommonError } from '../error';
import { debug } from '../logger';
import { CONFIG } from '../config';
import { PutNetErrCode, DeleteNetErrCode } from '../types';
import { uploader } from './uploader';
import { ErrorParser } from './parser';
import type { APIResponse, RequestConfig, RequestParams, ResponseBody, ServiceOption, ServiceRequestConfig, RefreshTokenResponse } from './types';
import type { LocalStore } from '../store';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export class Net {

  private readonly service: AxiosInstance;
  private readonly parser: ErrorParser;
  private local?: LocalStore;
  private _updating = false;
  private readonly _autoRefreshToken = true;
  private readonly _autoFetchCsrf = false;

  constructor() {
    this.parser = new ErrorParser();
    this.service = axios.create({
      withCredentials: true
    });
  }

  /**
   * 是否正在做更新操作，用于防止提交多个更新请求。
   * 目前，仅支持同时存在一个更新请求，如果需要支持存在多个更新请求，需要修改updating为计数器。
   */
  get updating(): boolean {
    return this._updating;
  }

  /**
   * 需要通过读取root store中保存的csrf-token信息
   * 所以在程序启动时（main的init中）进行设置，
   * 后续方法，均以root store正常被设置为前提。
   * @param root
   */
  init(local: LocalStore): void {
    // 设置请求地址，需要 config 初始化完成后才能设置，所以没有放在构造函数中
    this.service.defaults.baseURL = CONFIG.BACKEND_HOST;

    // 初始化上传工具
    this.local = local;
    uploader.init(local);

    // 设置请求拦截器及请求异常处理
    this.service.interceptors.request.use(
      this.requestInterceptor.bind(this),
      this.parser.parseRequestError.bind(this.parser)
    );

    // 设置响应拦截器及响应异常处理
    this.service.interceptors.response.use(
      this.responseInterceptor.bind(this),
      this.parser.parseResponseError.bind(this.parser)
    );
  }

  /**
   * 与post请求相同，当请求体过大无法用get完成时使用此方法
   * @param url
   * @param params
   * @param config
   * @returns
   */
  async fetch<T>(url: string, params?: unknown, config?: ServiceOption): Promise<ResponseBody<T>> {
    const { data } = await this.request<T>({ method: 'FETCH', url, data: params, ...config });
    return data.data;
  }

  // TODO: 删除post,get,put,delete的多余两个方法，仅保留一个
  async post<T>(config: ServiceRequestConfig): Promise<ResponseBody<T>>;
  async post<T>(path: string, params: unknown, config?: ServiceOption): Promise<ResponseBody<T>>;
  async post<T>(url: RequestParams, params?: unknown, option?: ServiceOption): Promise<ResponseBody<T>> {
    const config = typeof url === 'string' ? { ...option, url, data: params } : url;
    const { data } = await this.request<T>({ method: 'POST', ...config });
    return data.data;
  }

  async patch<T>(config: ServiceRequestConfig): Promise<ResponseBody<T>>;
  async patch<T>(path: string, params: unknown, config?: ServiceOption): Promise<ResponseBody<T>>;
  async patch<T>(url: RequestParams, params?: unknown, option?: ServiceOption): Promise<ResponseBody<T>> {
    const config = typeof url === 'string' ? { ...option, url, data: params } : url;
    const { data } = await this.request<T>({ method: 'PATCH', ...config });
    return data.data;
  }

  async put<T>(config: ServiceRequestConfig): Promise<ResponseBody<T>>;
  async put<T>(path: string, params: unknown, config?: ServiceOption): Promise<ResponseBody<T>>;
  async put<T>(url: RequestParams, params?: unknown, option?: ServiceOption): Promise<ResponseBody<T>> {
    const config = typeof url === 'string' ? { ...option, url, data: params } : url;
    const { data } = await this.request<T>({ method: 'PUT', ...config });
    const result = data.data;

    if (typeof result === 'number' && result <= 0) {
      throw new CommonError('The update operation is failed', PutNetErrCode.PUT_NET_ERROR);
    }
    return result;
  }

  async get<T>(config: ServiceRequestConfig): Promise<ResponseBody<T>>;
  async get<T>(path: string, params?: unknown, config?: ServiceOption): Promise<ResponseBody<T>>;
  async get<T>(url: RequestParams, params?: unknown, option?: ServiceOption): Promise<ResponseBody<T>> {
    const config = typeof url === 'string' ? { ...option, url, params } : url;
    const { data } = await this.request<T>({ method: 'GET', ...config });
    return data.data ?? data as T; // TODO: 后台有些接口数据没有放在data中
  }

  async delete<T>(config: ServiceRequestConfig): Promise<ResponseBody<T>>;
  async delete<T>(path: string, params: unknown, config?: ServiceOption): Promise<ResponseBody<T>>;
  async delete<T>(url: RequestParams, params?: unknown, option?: ServiceOption): Promise<ResponseBody<T>> {
    const config = typeof url === 'string' ? { ...option, url, data: params } : url;
    const { data } = await this.request<T>({ method: 'DELETE', ...config });
    const result = data.data;

    if (typeof result === 'number' && result <= 0) {
      throw new CommonError('The delete operation is failed', DeleteNetErrCode.DELETE_NET_ERROR);
    }
    return result;
  }

  // TODO: 名字不太好
  redirect(key: string): string {
    return `${CONFIG.BACKEND_HOST}/api/oss/redirect?name=${key}`;
  }

  /**
   * 下载请求
   */
  async download<T>(config: RequestConfig): Promise<T> {
    const res = await this.service.request({ responseType: 'blob', ...config });
    return res.data;
  }

  /**
   * 请求拦截器，设置csrf token到header
   * @param config
   * @returns
   */
  private async requestInterceptor(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    debug.infra('request interceptor');
    assert(this.local != null, 'local is not initialized');
    config.headers = config.headers ?? {};
    config.headers['X-Request-ID'] = lang.uuid();
    config.headers['X-Tenant-ID'] = this.local.code;
    config.headers['X-User-ID'] = this.local.id;

    if (this._autoRefreshToken) {
      const accessToken = await this.getToken(config);
      if (accessToken != null) {
        config.headers['X-Access-Token'] = accessToken;
      }
    }

    if (this._autoFetchCsrf && config.method !== 'get') {
      config.headers['csrf-token'] = await this.getCsrf();
    }

    debug.infra('headers', config.headers);
    return config;
  }

  private async getToken(config: InternalAxiosRequestConfig): Promise<string | undefined> {
    assert(this.local != null, 'local is not initialized');
    if (config.url == null) {
      return;
    }
    if (
      CONFIG.IGNORE_AUTH_API.includes(config.url)
    ) {
      return;
    }
    const { expireAt } = this.local;
    if (expireAt == null || expireAt <= Date.now()) {
      await this.refreshToken();
    }
    return this.local.token;
  }

  /**
   * 响应拦截器
   * 将页面逻辑错误转换成 CommonError 后抛出
   * @param response
   * @returns
   */
  private responseInterceptor(response: AxiosResponse): AxiosResponse {
    assert(this.local != null, 'local is not initialized');
    this.parser.parseError(response);
    this.local.csrf = response.headers.csrftoken;
    return response;
  }

  private async getCsrf(): Promise<string> {
    assert(this.local != null, 'local is not initialized');

    if (this.local.csrf == null) {
      const csrf = await this.fetchCsrf();
      this.local.csrf = csrf;
    }

    return this.local.csrf ?? '';
  }

  private async refreshToken(): Promise<void> {
    assert(this.local != null, 'local is not initialized');
    const response = await this.request({
      url: '/api/account/refreshToken',
      method: 'POST',
      params: { refreshToken: this.local.refreshToken },
      headers: { 'X-Access-Token': this.local.refreshToken },
      baseURL: CONFIG.BACKEND_MAIN_HOST
    });

    const result = response.data.data as RefreshTokenResponse;

    this.local.token = result.accessToken;
    this.local.refreshToken = result.refreshToken;
    this.local.expireAt = Date.now() + (result.expiresIn * 1000);
  }

  private async fetchCsrf(): Promise<string> {
    const response = await this.request({
      url: '/api/account/csrf',
      method: 'GET'
    });

    debug.infra('headers', response.headers);
    return response.headers.csrftoken ?? '';
  }

  private async request<T>(config: RequestConfig): Promise<AxiosResponse<APIResponse<T>>> {
    const { url, ...other } = config;

    try {
      if (['POST', 'DELETE', 'PUT'].includes(other.method)) {
        this._updating = true;
      }

      if (other.method === 'FETCH') {
        other.method = 'POST';
      }

      return await this.service.request({ url, ...other });
    } finally {
      this._updating = false;
    }
  }

}

export const net = new Net();
